import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faSave, faTrash, faPaperPlane, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { invoicesApi, type InvoiceLine, type Invoice } from '../api';
import InvoicePreview from './InvoicePreview';
import { clinicsService, type Patient } from '../../../services/clinicsService';
import ClinicsNewPatientModal from '../../../pages/clinics/ClinicsNewPatientModal';
import { GhostSelect } from '../../../components/ui/GhostSelect';
import BtnTaratic from '../../../components/ui/BtnTaratic';
import { useNotifications } from '../../../components/ui/notifications';

interface Props {
    invoiceToEdit?: Invoice | null;
    onSuccess: () => void;
    onCancel?: () => void;
}

const NewInvoiceForm: React.FC<Props> = ({ invoiceToEdit, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const [patientId, setPatientId] = useState<number | null>(null);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    const [invoiceType, setInvoiceType] = useState('F1 - Ordinaria');
    const [lines, setLines] = useState<InvoiceLine[]>([]);
    const [draftId, setDraftId] = useState<number | null>(null);

    // Current line edit
    const [currentLineStr, setCurrentLineStr] = useState({
        quantity: "1",
        unit_price: "0",
    });
    const [currentLine, setCurrentLine] = useState<InvoiceLine>({
        concept_name: '',
        quantity: 1,
        unit_price: 0,
        tax_rate: 7, // Default IGIC
        total: 0
    });

    const fetchPatients = async () => {
        try {
            const r = await clinicsService.getPatients({ per_page: 100 });
            setPatients(r.patients);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    useEffect(() => {
        if (invoiceToEdit) {
            setPatientId(invoiceToEdit.patient_id);
            if (invoiceToEdit.issue_date) {
                setIssueDate(invoiceToEdit.issue_date.split('T')[0]);
            }
            if (invoiceToEdit.type) {
                setInvoiceType(invoiceToEdit.type);
            }
            if (invoiceToEdit.lines) {
                setLines(invoiceToEdit.lines);
            }
            setDraftId(invoiceToEdit.id);
        } else {
            // Reset if no invoice to edit (e.g., clicked "Nueva Factura" directly)
            setPatientId(null);
            setIssueDate(new Date().toISOString().split('T')[0]);
            setInvoiceType('F1 - Ordinaria');
            setLines([]);
            setDraftId(null);
        }
    }, [invoiceToEdit]);

    const handleAddLine = () => {
        if (!currentLine.concept_name) return;

        const qty = parseFloat(currentLineStr.quantity.replace(',', '.')) || 0;
        const price = parseFloat(currentLineStr.unit_price.replace(',', '.')) || 0;
        const total = qty * price * (1 + currentLine.tax_rate / 100);

        setLines([...lines, {
            ...currentLine,
            quantity: qty,
            unit_price: price,
            total
        }]);

        setCurrentLine({ concept_name: '', quantity: 1, unit_price: 0, tax_rate: 7, total: 0 });
        setCurrentLineStr({ quantity: "1", unit_price: "0" });
    };

    const handleDeleteLine = (index: number) => {
        setLines(lines.filter((_, i) => i !== index));
    };

    const subtotal = lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
    const total = lines.reduce((acc, l) => acc + l.total, 0);

    const isFormValid = patientId !== null && lines.length > 0;

    const handleSaveDraft = async () => {
        if (!patientId) {
            notifications.warning('Selecciona un cliente', 'La factura necesita un cliente antes de guardarse.');
            return;
        }
        try {
            if (draftId) {
                await invoicesApi.deleteInvoice(draftId);
            }
            const invoice = await invoicesApi.createInvoice({
                patient_id: patientId,
                issue_date: issueDate,
                type: invoiceType,
                subtotal,
                total,
                lines
            });
            setDraftId(invoice.id);
            notifications.success('Borrador guardado', 'La factura se guardó como borrador.');
        } catch (e) {
            console.error(e);
            notifications.error('No se pudo guardar el borrador', 'Inténtalo de nuevo.');
        }
    };

    const handleEmit = async () => {
        if (!patientId) {
            notifications.warning('Selecciona un cliente', 'La factura necesita un cliente antes de emitirse.');
            return;
        }
        try {
            if (draftId) {
                await invoicesApi.deleteInvoice(draftId);
            }
            const invoice = await invoicesApi.createInvoice({
                patient_id: patientId,
                issue_date: issueDate,
                type: invoiceType,
                subtotal,
                total,
                lines
            });
            const emittedInvoice = await invoicesApi.emitInvoice(invoice.id);
            if (emittedInvoice.gcs_pdf_url) {
                window.open(emittedInvoice.gcs_pdf_url, '_blank');
            }
            notifications.success('Factura emitida', 'La factura se creó y emitió correctamente.');
            onSuccess();
        } catch (e) {
            console.error(e);
            notifications.error('No se pudo emitir la factura', 'Revisa los datos e inténtalo de nuevo.');
        }
    };

    const selectedPatient = patients.find(p => p.id === patientId);

    return (
        <div data-ui="new-invoice-form-region-001" className="flex flex-col lg:flex-row gap-8 relative z-10">
            {/* Left Column - Form */}
            <div data-ui="new-invoice-form-region-002" className="flex-1">
                <div data-ui="new-invoice-form-region-003" className="flex items-center gap-3 mb-4 min-h-[40px]">
                    {onCancel && (
                        <BtnTaratic onClick={onCancel} color="ghost-dark" size="md" iconOnly title="Volver">
                            <FontAwesomeIcon icon={faArrowLeft} className="text-white/60 hover:text-white transition-colors" />
                        </BtnTaratic>
                    )}
                    <h2 className="text-xl font-bold text-white">
                        {invoiceToEdit ? `Editar Factura ${invoiceToEdit.invoice_number || 'Borrador #' + invoiceToEdit.id}` : t('invoices.new.title')}
                    </h2>
                </div>
                <div data-ui="new-invoice-form-region-004" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6 space-y-6">

                    <div data-ui="new-invoice-form-region-005" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-ui="new-invoice-form-region-006" className="space-y-2">
                            <label className="text-sm font-semibold text-white/80">{t('invoices.new.form.client')}</label>
                            <div data-ui="new-invoice-form-region-007" className="relative">
                                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 z-10" />
                                <GhostSelect
                                    className="w-full pl-10"
                                    value={patientId ? String(patientId) : ''}
                                    onChange={(val: string) => setPatientId(val ? Number(val) : null)}
                                    options={[
                                        { value: "", label: t('invoices.new.form.searchClient') || "Buscar Cliente" },
                                        ...patients.map(p => ({ value: String(p.id), label: p.full_name }))
                                    ]}
                                />
                            </div>
                            <BtnTaratic type="button" onClick={() => setIsNewPatientModalOpen(true)} color="blue" size="md" className="w-full mt-2 flex items-center justify-center gap-2">
                                <FontAwesomeIcon icon={faPlus} />
                                <span>{t('invoices.new.form.addClient')}</span>
                            </BtnTaratic>
                        </div>
                        <div data-ui="new-invoice-form-region-008" className="space-y-2">
                            <label className="text-sm font-semibold text-white/80">{t('invoices.new.form.issueDate')}</label>
                            <input
                                type="date"
                                value={issueDate}
                                onChange={e => setIssueDate(e.target.value)}
                                className="mt-1 block w-full bg-white/5 border border-white/10 text-white placeholder-white/50 focus:bg-black/80 focus:outline-none focus:ring-1 focus:ring-white/30 rounded-2xl py-2 px-4 shadow-inner transition-all duration-300 [&::-webkit-calendar-picker-indicator]:invert"
                            />
                        </div>
                    </div>

                    <div data-ui="new-invoice-form-region-009" className="space-y-2">
                        <label className="text-sm font-semibold text-white/80">{t('invoices.new.form.invoiceType')}</label>
                        <GhostSelect
                            value={invoiceType}
                            onChange={(val: string) => setInvoiceType(val)}
                            className="w-full"
                            options={[
                                { value: "F1 - Ordinaria", label: "F1 - Ordinaria" },
                                { value: "F2 - Simplificada", label: "F2 - Simplificada" }
                            ]}
                        />
                    </div>

                    <div data-ui="new-invoice-form-region-010" className="border-t border-white/10 pt-6">
                        <h3 className="text-lg font-bold text-white mb-4">{t('invoices.new.lines.title')}</h3>

                        {/* New Line Input */}
                        <div data-ui="new-invoice-form-region-011" className="bg-white/5 p-4 rounded-xl border border-white/10 space-y-4">
                            <div data-ui="new-invoice-form-region-012" className="space-y-2">
                                <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">{t('invoices.new.lines.concept')}</label>
                                <input
                                    type="text"
                                    placeholder="Buscar o escribir concepto..."
                                    value={currentLine.concept_name}
                                    onChange={e => setCurrentLine({ ...currentLine, concept_name: e.target.value })}
                                    className="mt-1 block w-full bg-black/20 border border-white/10 text-white placeholder-white/50 focus:bg-black/80 focus:outline-none focus:ring-1 focus:ring-white/30 rounded-xl py-2 px-4 shadow-inner transition-all duration-300"
                                />
                            </div>

                            <div data-ui="new-invoice-form-region-013" className="grid grid-cols-3 gap-4">
                                <div data-ui="new-invoice-form-region-014" className="space-y-2">
                                    <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">{t('invoices.new.lines.quantity')}</label>
                                    <input
                                        type="text"
                                        value={currentLineStr.quantity}
                                        onChange={e => setCurrentLineStr({ ...currentLineStr, quantity: e.target.value })}
                                        className="mt-1 block w-full bg-black/20 border border-white/10 text-white placeholder-white/50 focus:bg-black/80 focus:outline-none focus:ring-1 focus:ring-white/30 rounded-xl py-2 px-4 shadow-inner transition-all duration-300"
                                    />
                                </div>
                                <div data-ui="new-invoice-form-region-015" className="space-y-2">
                                    <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">{t('invoices.new.lines.price')}</label>
                                    <div data-ui="new-invoice-form-region-016" className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 font-medium">€</span>
                                        <input
                                            type="text"
                                            value={currentLineStr.unit_price}
                                            onChange={e => setCurrentLineStr({ ...currentLineStr, unit_price: e.target.value })}
                                            className="mt-1 block w-full pl-8 pr-4 py-2 bg-black/20 border border-white/10 text-white placeholder-white/50 focus:bg-black/80 focus:outline-none focus:ring-1 focus:ring-white/30 rounded-xl shadow-inner transition-all duration-300"
                                        />
                                    </div>
                                </div>
                                <div data-ui="new-invoice-form-region-017" className="space-y-2">
                                    <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">{t('invoices.new.lines.tax')}</label>
                                    <GhostSelect
                                        value={String(currentLine.tax_rate)}
                                        onChange={(val: string) => setCurrentLine({ ...currentLine, tax_rate: Number(val) })}
                                        className="w-full"
                                        options={[
                                            { value: "0", label: "0%" },
                                            { value: "3", label: "IGIC 3%" },
                                            { value: "7", label: "IGIC 7%" },
                                            { value: "21", label: "IVA 21%" }
                                        ]}
                                    />
                                </div>
                            </div>

                            <div data-ui="new-invoice-form-region-018" className="flex justify-between items-center pt-2">
                                <div data-ui="new-invoice-form-region-019" className="text-sm font-bold text-white">
                                    {t('invoices.new.lines.total')}: {((parseFloat(currentLineStr.quantity.replace(',', '.')) || 0) * (parseFloat(currentLineStr.unit_price.replace(',', '.')) || 0) * (1 + currentLine.tax_rate / 100)).toFixed(2)} €
                                </div>
                                <BtnTaratic
                                    onClick={handleAddLine}
                                    color="blue"
                                    size="md"
                                    className="flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>{t('invoices.new.lines.add')}</span>
                                </BtnTaratic>
                            </div>
                        </div>

                        {/* List of lines */}
                        <div data-ui="new-invoice-form-region-020" className="mt-4 space-y-2">
                            {lines.map((l, i) => (
                                <div data-ui="new-invoice-form-region-021" key={i} className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/10 text-sm">
                                    <div data-ui="new-invoice-form-region-022" className="flex-1">
                                        <div data-ui="new-invoice-form-region-023" className="font-semibold text-white">{l.concept_name}</div>
                                        <div data-ui="new-invoice-form-region-024" className="text-white/60 text-xs">{l.quantity} x {l.unit_price.toFixed(2)}€ (Imp: {l.tax_rate}%)</div>
                                    </div>
                                    <div data-ui="new-invoice-form-region-025" className="font-bold text-white mr-4">{l.total.toFixed(2)}€</div>
                                    <BtnTaratic onClick={() => handleDeleteLine(i)} color="red" size="xs" iconOnly title="Eliminar línea">
                                        <FontAwesomeIcon icon={faTrash} />
                                    </BtnTaratic>
                                </div>
                            ))}
                        </div>

                        {/* Totals */}
                        <div data-ui="new-invoice-form-region-026" className="mt-6 flex justify-end">
                            <div data-ui="new-invoice-form-region-027" className="bg-white/5 p-4 rounded-xl border border-white/10 min-w-[200px] text-right shadow-inner">
                                <div data-ui="new-invoice-form-region-028" className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">{t('invoices.new.lines.total')}</div>
                                <div data-ui="new-invoice-form-region-029" className="text-3xl font-bold text-white">€{total.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Column - Preview */}
            <div data-ui="new-invoice-form-region-030" className="flex-1">
                <div data-ui="new-invoice-form-region-031" className="flex justify-between items-center mb-4 min-h-[40px]">
                    <h2 className="text-xl font-bold text-white">{t('invoices.preview.title')}</h2>
                    <div data-ui="new-invoice-form-region-032" className="flex gap-3">
                        <BtnTaratic
                            onClick={handleSaveDraft}
                            disabled={!isFormValid}
                            color="green"
                            size="md"
                            className="flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faSave} />
                            <span>{t('invoices.new.actions.saveDraft')}</span>
                        </BtnTaratic>
                        <BtnTaratic
                            onClick={handleEmit}
                            disabled={!isFormValid}
                            color="blue"
                            size="md"
                            className="flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                            <span>{t('invoices.new.actions.emit')}</span>
                        </BtnTaratic>
                    </div>
                </div>

                <InvoicePreview
                    issueDate={issueDate}
                    invoiceType={invoiceType}
                    patientName={selectedPatient?.full_name || 'Sin seleccionar'}
                    lines={lines}
                    subtotal={subtotal}
                    total={total}
                />
            </div>

            {isNewPatientModalOpen && (
                <ClinicsNewPatientModal
                    onClose={() => setIsNewPatientModalOpen(false)}
                    onSuccess={async (newPatientId) => {
                        setIsNewPatientModalOpen(false);
                        await fetchPatients();
                        if (newPatientId) {
                            setPatientId(newPatientId);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default NewInvoiceForm;
